<template>
  <div class="earnings-page">
    <h2>推荐收益</h2>
    <el-skeleton v-if="loading" rows="4" animated />
    <template v-else-if="summary">
      <el-row :gutter="16" class="stats">
        <el-col :xs="24" :sm="12">
          <el-statistic title="已获绑定礼遇（元）" :value="centsToYuan(summary.refereeBonusTotalCents)" :precision="2" />
        </el-col>
        <el-col :xs="24" :sm="12">
          <el-statistic title="推荐绑定人数" :value="summary.directBindingsCount" />
        </el-col>
        <el-col :xs="24" :sm="12">
          <el-statistic title="已发放礼遇（元）" :value="centsToYuan(summary.referrerBonusTotalCents)" :precision="2" />
        </el-col>
        <el-col v-if="summary.isPartner" :xs="24" :sm="12">
          <el-statistic title="专属权益·已到账（元）" :value="centsToYuan(summary.partnerSettledCents)" :precision="2" />
        </el-col>
        <el-col v-if="summary.isPartner" :xs="24" :sm="12">
          <el-statistic title="专属权益·预估（元）" :value="centsToYuan(summary.partnerEstimatedCents)" :precision="2" />
        </el-col>
        <el-col v-if="summary.isPartner" :xs="24" :sm="12">
          <el-statistic title="专属权益·待到账（元）" :value="centsToYuan(summary.partnerPendingCents)" :precision="2" />
        </el-col>
      </el-row>
      <p v-if="summary.isPartner" class="partner-tag">
        <el-tag type="warning">已开通臻享资格</el-tag>
        <span class="hint inline-hint">分润比例 {{ partnerRate }}%；申诉期结束后 T+{{ partnerSettleDays }} 入账</span>
      </p>
      <p v-if="summary.isPartner" class="hint scope-hint">{{ PARTNER_COMMISSION_SCOPE_HINT }}</p>
      <p v-else class="hint">臻享资格由管理员开通；开通后可设置臻享礼券并享有专属权益。</p>
    </template>

    <el-card v-if="bindStatus && !bindStatus.hasBinding && bindStatus.canBind" class="bind-card" shadow="never">
      <h3>尚未绑定礼券？</h3>
      <p class="hint">注册后可在此绑定他人分享的礼券，终身仅可绑定一张。</p>
      <el-button type="primary" link @click="$router.push('/referral/bind-coupon')">前往绑定页</el-button>
    </el-card>

    <el-card shadow="never" class="list-card">
      <template #header>
        <span>我推荐的用户</span>
        <el-radio-group v-model="bindingFilter" size="small" class="header-filter" @change="onBindingFilterChange">
          <el-radio-button label="">全部</el-radio-button>
          <el-radio-button label="partner">臻享客户</el-radio-button>
        </el-radio-group>
      </template>
      <el-table :data="bindings" v-loading="loadingBindings" empty-text="暂无推荐绑定">
        <el-table-column label="用户" min-width="120">
          <template #default="s">
            {{ s.row.refereeNickname || s.row.refereeUsername || s.row.refereeUserId }}
          </template>
        </el-table-column>
        <el-table-column prop="couponNo" label="券号" width="140" />
        <el-table-column prop="couponType" label="类型" width="100">
          <template #default="s">{{ couponTypeLabel(s.row.couponType) }}</template>
        </el-table-column>
        <el-table-column label="绑定时间" width="170">
          <template #default="s">{{ formatTime(s.row.createdAt) }}</template>
        </el-table-column>
        <el-table-column prop="bonusPaid" label="礼遇已发" width="90">
          <template #default="s">{{ s.row.bonusPaid === 1 ? '是' : '否' }}</template>
        </el-table-column>
      </el-table>
      <el-pagination
        v-if="bindingsTotal > bindingsPageSize"
        class="pager"
        layout="prev, pager, next"
        :total="bindingsTotal"
        :page-size="bindingsPageSize"
        :current-page="bindingsPage"
        @current-change="onBindingsPage"
      />
    </el-card>

    <el-card v-if="summary?.isPartner" shadow="never" class="list-card">
      <template #header>臻享客户分润汇总</template>
      <el-table :data="partnerCustomers" v-loading="loadingPartnerCustomers" empty-text="暂无客户消费">
        <el-table-column label="客户" min-width="120">
          <template #default="s">{{ s.row.refereeNickname || s.row.refereeUsername || s.row.refereeUserId }}</template>
        </el-table-column>
        <el-table-column prop="orderCount" label="订单数" width="80" />
        <el-table-column label="分润合计（元）" width="120">
          <template #default="s">{{ centsToYuan(s.row.commissionCents) }}</template>
        </el-table-column>
        <el-table-column label="预估（元）" width="100">
          <template #default="s">{{ centsToYuan(s.row.estimatedCents) }}</template>
        </el-table-column>
        <el-table-column label="待到账（元）" width="110">
          <template #default="s">{{ centsToYuan(s.row.pendingCents) }}</template>
        </el-table-column>
        <el-table-column label="已到账（元）" width="110">
          <template #default="s">{{ centsToYuan(s.row.settledCents) }}</template>
        </el-table-column>
      </el-table>
    </el-card>

    <el-card v-if="summary?.isPartner" shadow="never" class="list-card">
      <template #header>专属分润流水</template>
      <el-table :data="commissions" v-loading="loadingCommission" empty-text="暂无分润记录">
        <el-table-column prop="orderNo" label="订单号" min-width="160" show-overflow-tooltip />
        <el-table-column label="订单金额（元）" width="120">
          <template #default="s">{{ centsToYuan(s.row.orderAmountCents) }}</template>
        </el-table-column>
        <el-table-column label="分润（元）" width="100">
          <template #default="s">{{ centsToYuan(s.row.commissionCents) }}</template>
        </el-table-column>
        <el-table-column prop="status" label="状态" width="90">
          <template #default="s">{{ commissionStatusLabel(s.row.status) }}</template>
        </el-table-column>
        <el-table-column label="时间" width="170">
          <template #default="s">{{ formatTime(s.row.createdAt) }}</template>
        </el-table-column>
      </el-table>
      <el-pagination
        v-if="commissionTotal > commissionPageSize"
        class="pager"
        layout="prev, pager, next"
        :total="commissionTotal"
        :page-size="commissionPageSize"
        :current-page="commissionPage"
        @current-change="onCommissionPage"
      />
    </el-card>

    <el-button @click="$router.push('/referral/coupons')">管理我的礼券</el-button>
    <el-button v-if="summary?.isPartner" type="primary" plain @click="exportStatement">导出对账单 CSV</el-button>
  </div>
</template>

<script>
import axios from 'axios'
import {
  couponTypeLabel,
  commissionStatusLabel,
  fetchMyPartnerCommission,
  fetchMyPartnerCustomers,
  fetchMyReferralBindings,
  fetchReferralSettings,
  fetchReferralBindStatus,
  PARTNER_COMMISSION_SCOPE_HINT
} from '@/utils/referralCoupon'

export default {
  name: 'ReferralEarningsView',
  data() {
    return {
      PARTNER_COMMISSION_SCOPE_HINT,
      loading: true,
      summary: null,
      bindings: [],
      bindingsTotal: 0,
      bindingsPage: 1,
      bindingsPageSize: 10,
      loadingBindings: false,
      commissions: [],
      commissionTotal: 0,
      commissionPage: 1,
      commissionPageSize: 10,
      loadingCommission: false,
      partnerCustomers: [],
      loadingPartnerCustomers: false,
      bindingFilter: '',
      bindStatus: null,
      partnerRate: 10,
      partnerSettleDays: 7
    }
  },
  created() {
    if (!localStorage.getItem('jwt_token')) {
      this.$goToLogin(this.$route.fullPath)
      return
    }
    this.initPage()
  },
  methods: {
    couponTypeLabel,
    commissionStatusLabel,
    centsToYuan(cents) {
      return Number(cents || 0) / 100
    },
    formatTime(ts) {
      if (!ts) return '—'
      return new Date(ts * 1000).toLocaleString()
    },
    async initPage() {
      this.loading = true
      try {
        await this.loadSummary()
        try {
          this.bindStatus = await fetchReferralBindStatus()
        } catch { /* ignore */ }
        try {
          const settings = await fetchReferralSettings()
          this.partnerRate = settings.partnerCommissionRate || 10
          this.partnerSettleDays = settings.partnerCommissionSettleDays || 7
        } catch { /* ignore */ }
        await this.loadBindings()
        if (this.summary?.isPartner) {
          await this.loadCommission()
          await this.loadPartnerCustomers()
        }
      } finally {
        this.loading = false
      }
    },
    async loadSummary() {
      const res = await axios.post('/api/v1/referral/earnings/summary', {})
      if (res.data.code === 200) {
        this.summary = res.data.data
      }
    },
    async loadBindings() {
      this.loadingBindings = true
      try {
        const data = await fetchMyReferralBindings(this.bindingsPage, this.bindingsPageSize, this.bindingFilter)
        this.bindings = data.bindings || []
        this.bindingsTotal = data.total || 0
      } catch (e) {
        this.$message.error(e.message || '加载推荐列表失败')
      } finally {
        this.loadingBindings = false
      }
    },
    async loadCommission() {
      this.loadingCommission = true
      try {
        const data = await fetchMyPartnerCommission(this.commissionPage, this.commissionPageSize)
        this.commissions = data.items || []
        this.commissionTotal = data.total || 0
      } catch (e) {
        this.$message.error(e.message || '加载分润流水失败')
      } finally {
        this.loadingCommission = false
      }
    },
    onBindingsPage(p) {
      this.bindingsPage = p
      this.loadBindings()
    },
    onBindingFilterChange() {
      this.bindingsPage = 1
      this.loadBindings()
    },
    async loadPartnerCustomers() {
      this.loadingPartnerCustomers = true
      try {
        const data = await fetchMyPartnerCustomers(1, 50)
        this.partnerCustomers = data.items || []
      } catch (e) {
        this.$message.error(e.message || '加载客户汇总失败')
      } finally {
        this.loadingPartnerCustomers = false
      }
    },
    onCommissionPage(p) {
      this.commissionPage = p
      this.loadCommission()
    },
    exportStatement() {
      const token = localStorage.getItem('jwt_token')
      if (!token) {
        this.$message.warning('请先登录')
        return
      }
      window.open(`/api/v1/referral/export/statement`, '_blank')
    }
  }
}
</script>

<style scoped>
.earnings-page { max-width: none; width: 100%; margin: 0 auto; padding: 24px 20px 40px; box-sizing: border-box; }
.stats { margin: 20px 0; }
.hint { color: #606266; font-size: 14px; }
.inline-hint { margin-left: 8px; }
.bind-card { margin: 24px 0; }
.bind-card h3 { margin: 0 0 8px; font-size: 1rem; }
.list-card { margin: 16px 0; }
.partner-tag { margin-top: 12px; display: flex; align-items: center; flex-wrap: wrap; gap: 8px; }
.pager { margin-top: 12px; justify-content: center; }
.header-filter { float: right; }
</style>
